import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Track } from '@app/core/models/Track';
import { notOnlySpacesRegExp } from '@app/shared/custom-validators';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched } from '@ngxs/store';
import { SchedulerTrack } from '@radioking/scheduler-core';
import {
  ErrorFormFormat,
  FormHelperService,
  MobileSidenavService,
  UiStoreHelperService,
} from '@radioking/shared/common-services';
import { CloseCurrentPanel, DynamicEnabledPanel } from '@radioking/shared/pannel-manager';
import { InputColorPickerComponent } from '@radioking/ui/input-color-picker';
import amplitude from 'amplitude-js';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { Program } from '../../../models/programs.model';
import {
  CreateProgramFailure,
  CreateProgramRequest,
  CreateProgramSuccess,
  HideIntroExtro,
} from '../../../states/programs.actions';

export type IntroOrExtro = 'intro' | 'extro';

@Component({
  selector: 'rk-create-program',
  templateUrl: './create-program.component.html',
  styleUrls: ['./create-program.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateProgramComponent
  implements OnInit, AfterViewInit, OnDestroy, DynamicEnabledPanel
{
  createProgramForm: UntypedFormGroup;
  createProgramFormErrors: ErrorFormFormat;

  $addingType: WritableSignal<IntroOrExtro> = signal(null);
  search$ = new BehaviorSubject<string>('');

  subscription = new Subscription();

  isSaving$: Observable<boolean>;

  // Used to detect if opened on mobile
  mobileData: string;

  readonly autoFocusDelayMs = 450; // Wait until sidenav transition end

  constructor(
    private readonly formHelper: FormHelperService,
    private readonly fb: UntypedFormBuilder,
    private readonly helper: UiStoreHelperService,
    private readonly actions$: Actions,
    private readonly mobileService: MobileSidenavService,
    private readonly elRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.isSaving$ = this.helper.getLoadingState(
      [CreateProgramRequest],
      [CreateProgramSuccess, CreateProgramFailure],
    );
    this.subscription.add(
      this.actions$.pipe(ofActionDispatched(HideIntroExtro)).subscribe(() => {
        this.hideModalOrIntro();
      }),
    );
  }

  ngAfterViewInit() {
    if (this.mobileData && this.elRef) {
      this.elRef.nativeElement.classList.add('mobile');
    }
  }

  hideModalOrIntro() {
    if (this.$addingType() !== null) {
      this.$addingType.set(null);
    } else {
      this.mobileService.close(true);
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  createForm() {
    this.createProgramFormErrors = {
      name: {},
      color: {},
    };
    this.createProgramForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern(notOnlySpacesRegExp)]],
      color: [InputColorPickerComponent.getRandomColor(), Validators.required],
      intro: null,
      extro: null,
    });

    this.subscription.add(
      this.createProgramForm.valueChanges.subscribe(() => {
        this.createProgramForm.clearValidators();
        this.formHelper.updateErrorMapping(
          this.createProgramForm,
          this.createProgramFormErrors,
        );
      }),
    );
    this.formHelper.updateErrorMapping(
      this.createProgramForm,
      this.createProgramFormErrors,
    );
  }

  @Dispatch()
  submit() {
    amplitude.getInstance().logEvent('new_program');
    const program: Partial<Program> = {
      name: this.createProgramForm.value['name'],
      color: this.createProgramForm.value['color'],
      idTrackIntro: this.createProgramForm.value['intro']?.id,
      idTrackExtro: this.createProgramForm.value['extro']?.id,
    };

    return new CreateProgramRequest(program);
  }

  @Dispatch()
  cancel = () => new CloseCurrentPanel(true);

  @HostBinding('class.no-actions') get f() {
    return this.$addingType() !== null;
  }

  hasToShowConfirmOnExit(): boolean {
    return false;
  }

  isDirty(): boolean {
    return false;
  }

  setIntro(track: Track | SchedulerTrack) {
    this.createProgramForm.setValue({
      ...this.createProgramForm.value,
      intro: track,
    });
  }

  setExtro(track: Track | SchedulerTrack) {
    this.createProgramForm.setValue({
      ...this.createProgramForm.value,
      extro: track,
    });
  }

  onChangeSearch(str: string) {
    this.search$.next(str);
  }

  openTrackSelector(e: Event, type: IntroOrExtro) {
    (e.target as HTMLInputElement).blur();
    this.$addingType.set(type);
  }

  applyTrack(track: SchedulerTrack) {
    if (this.$addingType() === 'intro') {
      this.setIntro(track);
    } else {
      this.setExtro(track);
    }
    this.$addingType.set(null);
  }
}
